import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Button,
  Alert,
  Spinner,
  Card,
  Table,
  ProgressBar,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
// import "./styles.module.css";
import { Box } from "./brokenLinkStyle";
import { ToolsHeader } from "../components/ToolsHeader";
import { isInIframe } from "../../utils/is-in-iframe";

interface CheckResult {
  status: string;
  url: string;
  anchorText: string;
  linkedFrom: string;
  isBroken?: boolean;
  type?: string;
}

const getProblematicLinks = (results: CheckResult[]) =>
  results.filter(
    (link) =>
      link.status.startsWith("404") ||
      link.status.startsWith("410")
  );


const BrokenLinkChecker: React.FC = () => {
  const [url, setUrl] = useState<string>("");
  const [checkMode, setCheckMode] = useState<"single" | "whole">("single");
  const [allResults, setAllResults] = useState<CheckResult[]>([]);
  const [displayedResults, setDisplayedResults] = useState<CheckResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);
  const [totalUrls, setTotalUrls] = useState<number>(0);
  const [checkedUrls, setCheckedUrls] = useState<number>(0);
  const [okCount, setOkCount] = useState<number>(0);
  const [failedCount, setFailedCount] = useState<number>(0);
  const [botBlockedCount, setBotBlockedCount] = useState<number>(0);
  const [sslErrorCount, setSslErrorCount] = useState<number>(0);
  const [nonSeoIssuesCount, setNonSeoIssuesCount] = useState<number>(0);
  const [showFullReport, setShowFullReport] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [scanCompleted, setScanCompleted] = useState<boolean>(false);
  const tableRef = useRef<HTMLTableElement>(null);


  const handleSubmit = async (e: React.FormEvent) => {
    
    e.preventDefault();
    if (isChecking || !url) return;

    setIsChecking(true);
    setIsLoading(true);
    setError("");
    setAllResults([]);
    setDisplayedResults([]);
    setCheckedUrls(0);
    setTotalUrls(0);
    setOkCount(0);
    setFailedCount(0);
    setBotBlockedCount(0);
    setSslErrorCount(0);
    setProgress(0);
    setShowFullReport(false);
    setScanCompleted(false);


    try {
      const baseUrl = process.env.REACT_APP_BASE_API!;

      if (checkMode === "single") {
        const apiUrl = `${baseUrl}/api/site-map?url=${encodeURIComponent(url)}`;
        const response = await fetch(apiUrl);
        const data = await response.json();
        console.log(" Raw API response:", data);


        if (!response.ok || !data.success || !Array.isArray(data.data)) {
          throw new Error("Failed to fetch or parse links.");
        }

        const linkResults: CheckResult[] = data.data.map((item: any) => {
          const isActuallyBroken =
            item.status.startsWith("404") ||
            item.status.startsWith("410");


          return {
            url: item.link,
            status: item.status,
            isBroken: isActuallyBroken,
            anchorText: item.anchorText,
            linkedFrom: url,
            type:
              item.status.startsWith("403")
                ? "Bot Blocked (Not an SEO issue)"
                : item.status.startsWith("4") || item.status.startsWith("5")
                  ? "HTTP Error"
                  : item.status.includes("SSL")
                    ? "SSL Error"
                    : item.status.includes("DNS")
                      ? "DNS Error"
                      : item.status.includes("Timeout") || item.status.includes("Network")
                        ? "Network Error"
                        : "Other",
          };
        });


        setAllResults(linkResults);
        setDisplayedResults(getProblematicLinks(linkResults));
        setScanCompleted(true);



        setTotalUrls(linkResults.length);
        setCheckedUrls(linkResults.length);
        setOkCount(linkResults.filter((l) => !l.isBroken && !l.status.includes('Bot Blocked') && !l.status.includes('SSL')).length);
        setFailedCount(linkResults.filter((l) => l.status.startsWith("404") || l.status.startsWith("410")).length);

        setBotBlockedCount(linkResults.filter((l) => l.status.startsWith("403") && l.type?.includes("Bot Blocked")).length);
        setSslErrorCount(linkResults.filter((l) => l.status.startsWith('-1') || l.status.includes('SSL')).length);
        setNonSeoIssuesCount(linkResults.filter((l) => l.status.includes("Connection") || l.status.includes("Timeout") || l.status.includes("Network")).length);

        setProgress(100);
      } else {
        // Whole site mode (apply same logic)
        const apiUrl = `${baseUrl}/api/site-map/allLink?url=${encodeURIComponent(url)}`;
        const response = await fetch(apiUrl);
        const data2 = await response.json();


        if (data2.data && Array.isArray(data2.data)) {
          const totalPages = data2.data.length;
          let runningResults: CheckResult[] = [];
          const concurrency = 5;

          for (let i = 0; i < totalPages; i += concurrency) {
            const batch = data2.data.slice(i, i + concurrency);
            const batchPromises = batch.map(async (pageUrl: string) => {
              try {
                const eUrl = `${baseUrl}/api/site-map?url=${encodeURIComponent(pageUrl)}`;
                const res = await fetch(eUrl);
                const resData = await res.json();

                if (!res.ok || !resData.success || !Array.isArray(resData.data)) {
                  return [];
                }

                return resData.data.map((item: any) => {
                  const isActuallyBroken =
                    item.status.startsWith("404") ||
                    item.status.startsWith("410");


                  return {
                    url: item.link,
                    status: item.status,
                    isBroken: isActuallyBroken,
                    anchorText: item.anchorText || "(no anchor text)",
                    linkedFrom: pageUrl,
                    type:
                      item.status.startsWith("403")
                        ? "Bot Blocked (Not an SEO issue)"
                        : item.status.startsWith("4") || item.status.startsWith("5")
                          ? "HTTP Error"
                          : item.status.includes("SSL")
                            ? "SSL Error"
                            : item.status.includes("DNS")
                              ? "DNS Error"
                              : item.status.includes("Timeout") || item.status.includes("Network")
                                ? "Network Error"
                                : "Other",
                  };
                });
              } catch (err) {
                return [];
              }
            });



            const batchResults = await Promise.all(batchPromises);
            const flatBatch = batchResults.flat();
            runningResults = [...runningResults, ...flatBatch];

            const uniqueResults = Array.from(
              new Map(runningResults.map((item) => [item.url, item])).values()
            );

            setAllResults(uniqueResults);
            setDisplayedResults(getProblematicLinks(uniqueResults)); // ✅ broken first


            setCheckedUrls(uniqueResults.length);
            setOkCount(uniqueResults.filter((l) => !l.isBroken && !l.status.includes('Bot Blocked') && !l.status.includes('SSL')).length);
            setFailedCount(uniqueResults.filter((l) => l.isBroken).length);

            setBotBlockedCount(uniqueResults.filter((l) => l.status.startsWith("403") && l.type?.includes("Bot Blocked")).length);
            setSslErrorCount(uniqueResults.filter((l) => l.status.startsWith('-1') || l.status.includes('SSL')).length);
            setNonSeoIssuesCount(uniqueResults.filter((l) => l.status.includes("Connection") || l.status.includes("Timeout") || l.status.includes("Network")).length);


            const checkedPages = Math.min(i + concurrency, totalPages);
            const progressPercent = Math.round((checkedPages / totalPages) * 100);
            setProgress(progressPercent);
          }

          const uniqueFinal = Array.from(
            new Map(runningResults.map((item) => [item.url, item])).values()
          );
          setAllResults(uniqueFinal);
          setDisplayedResults(getProblematicLinks(uniqueFinal));

          setTotalUrls(uniqueFinal.length);
          setCheckedUrls(uniqueFinal.length);
          setOkCount(uniqueFinal.filter((l) => !l.isBroken && !l.status.includes('Bot Blocked') && !l.status.includes('SSL')).length);
          setFailedCount(uniqueFinal.filter((l) => l.isBroken).length);

          setBotBlockedCount(uniqueFinal.filter((l) => l.status.startsWith("403") && l.type?.includes("Bot Blocked")).length);
          setSslErrorCount(uniqueFinal.filter((l) => l.status.startsWith('-1') || l.status.includes('SSL')).length);
          setNonSeoIssuesCount(uniqueFinal.filter((l) => l.status.includes("Connection") || l.status.includes("Timeout") || l.status.includes("Network")).length);


          setProgress(100);
          setScanCompleted(true);

        } else {
          throw new Error("No pages found for analysis.");
        }
      }

      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsChecking(false);
    }
  };

  const handleFullReport = () => {
    setShowFullReport(true);
    setDisplayedResults(allResults); // ✅ show EVERYTHING
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };


  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [displayedResults]);

  const renderStatus = () => {
    if (!isLoading && allResults.length > 0) {
      return (
        <div className="status">
          <p>
            Pages scanned: {checkMode === "whole" ? Math.min(totalUrls, 50) : 1} ·{" "}
            Links checked: {checkedUrls},{" "}
            {okCount} OK ·{" "}
            {failedCount} true broken (404/410 only) ·{" "}
            {botBlockedCount} bot blocked ·{" "}
            {nonSeoIssuesCount} non-SEO issues
          </p>

          <p>Generated: {new Date().toLocaleString()}</p>
          {!showFullReport && (
            <Button
              variant="outline-primary"
              onClick={handleFullReport}
              className="fullReportButton"
            >
              Full report
            </Button>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Box className={`${!isInIframe ? 'py-5 defaultText' : 'py-1 interDisplay'}`}>
        {!isInIframe && (
          <ToolsHeader
            title="Site Checker: Free Broken Link Tool"
            description="Quickly identify and fix broken links on your website to boost
                SEO and user experience."
          />
        )}
        <Card className="container">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="url" className="mb-3">
                <Form.Control
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                  disabled={isLoading}
                  className="input"
                  placeholder="Enter website URL (e.g., https://example.com)"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  inline
                  label="Check single webpage"
                  name="checkMode"
                  type="radio"
                  id="check-single"
                  checked={checkMode === "single"}
                  onChange={() => setCheckMode("single")}
                />
                <Form.Check
                  inline
                  label="Check whole website"
                  name="checkMode"
                  type="radio"
                  id="check-whole"
                  checked={checkMode === "whole"}
                  onChange={() => setCheckMode("whole")}
                />
              </Form.Group>

              <Button
                variant="warning"
                type="submit"
                disabled={isLoading || !url}
                className="submitButton"
                style={{ backgroundColor: "#8cc540", color: "#fff" }}
              >
                {isLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Checking...
                  </>
                ) : (
                  "Check"
                )}
              </Button>
            </Form>

            {isLoading && (
              <ProgressBar
                now={progress}
                label={`${progress}%`}
                className="mt-3"
              />
            )}

            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}

            {renderStatus()}

            {/* ℹ️ No links found – JS rendered page info */}
            {scanCompleted &&
              checkMode === "single" &&
              allResults.length === 0 && (
                <Alert variant="info" className="mt-3 d-flex align-items-center">
                  <span>No links found.</span>

                  <OverlayTrigger
                    placement="right"
                    overlay={
                      <Tooltip id="js-links-tooltip">
                        Some pages load links via JavaScript and cannot be crawled without a browser.
                        <br />
                        Try <strong>“Check whole website”</strong> for better results.
                      </Tooltip>
                    }
                  >
                    <span
                      className="ms-2 text-primary"
                      style={{ cursor: "pointer", fontWeight: 600 }}
                    >
                      ⓘ
                    </span>
                  </OverlayTrigger>
                </Alert>
              )}

            {displayedResults.length > 0 && (
              <div style={{ width: "100%", overflow: "auto" }}>
                <Table striped bordered hover className="table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Type</th>
                      <th>URL</th>
                      <th>Anchor Text</th>
                      <th>Linked From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedResults.map((result, index) => (
                      <tr
                        key={index}
                        className={
                          result.isBroken
                            ? "broken"
                            : result.status === "Checking"
                              ? "checking"
                              : ""
                        }
                      >
                        <td>{result.status}</td>
                        <td>{result.type}</td>
                        <td>
                          <a
                            href={result.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {result.url}
                          </a>
                        </td>
                        <td>{result.anchorText}</td>
                        <td>{result.linkedFrom}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Box>
    </>
  );
};

export default BrokenLinkChecker;